import {
  Document,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import {
  ObjectDeviationMetadataType,
  ObjectDeviationType,
} from "../../types/ObjectDeviation";
import { formatFinnishDate } from "../../utilities/PdfUtils";
import { ReactNode } from "react";

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: "#111827",
  },
  reportTitle: {
    backgroundColor: "#d1d5db",
    borderWidth: 1,
    borderColor: "#374151",
    padding: 8,
    fontSize: 14,
    fontWeight: "bold",
    textAlign: "center",
  },
  company: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#374151",
    padding: 7,
    fontWeight: "bold",
  },
  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#374151",
    padding: 6,
  },
  infoCell: {
    width: "50%",
    paddingVertical: 3,
    paddingRight: 5,
  },
  section: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#374151",
  },
  sectionTitle: {
    backgroundColor: "#e5e7eb",
    padding: 6,
    fontWeight: "bold",
    fontSize: 10,
  },
  sectionBody: {
    padding: 7,
  },
  optionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  option: {
    width: "50%",
    paddingVertical: 3,
    paddingRight: 4,
  },
  fieldLabel: {
    fontWeight: "bold",
    marginBottom: 3,
  },
  fieldValue: {
    minHeight: 20,
    marginBottom: 6,
  },
  signatureRow: {
    flexDirection: "row",
    gap: 18,
    paddingTop: 28,
  },
  signature: {
    width: "50%",
    borderTopWidth: 1,
    borderColor: "#374151",
    paddingTop: 3,
  },
});

interface DeviationReportPDFProps {
  deviation: ObjectDeviationType;
  metadata: ObjectDeviationMetadataType;
  getTranslation: (key: string) => string | null;
}

const optionMark = (selected: boolean) => (selected ? "[X]" : "[ ]");

export default function DeviationReportPDF({
  deviation,
  metadata,
  getTranslation,
}: Readonly<DeviationReportPDFProps>) {
  const section = (
    number: number,
    title: string,
    content: ReactNode,
  ) => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>
        {number}. {title}
      </Text>
      <View style={styles.sectionBody}>{content}</View>
    </View>
  );

  const option = (selected: boolean, label: string) => (
    <Text style={styles.option}>
      {optionMark(selected)} {label}
    </Text>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.reportTitle}>
          {getTranslation("DEVIATION_REPORT_TITLE") || "DEVIATION REPORT"}
        </Text>
        <Text style={styles.company}>
          {deviation.contractorName ||
            getTranslation("DEVIATION_REPORT_COMPANY") ||
            "Contractor"}
        </Text>
        <View style={styles.infoGrid}>
          <Text style={styles.infoCell}>
            {getTranslation("DEVIATION_REPORT_CONTRACT") ||
              "Project / Contract"}
            : {deviation.contractName}
          </Text>
          <Text style={styles.infoCell}>
            {getTranslation("DEVIATION_REPORT_LOCATION") ||
              "Object no. / location"}
            : {deviation.objectLocation}
          </Text>
          <Text style={styles.infoCell}>
            {getTranslation("DEVIATION_REPORT_FOREMAN") ||
              "Contractor foreman"}
            : {deviation.contractorForemanName || ""}
          </Text>
          <Text style={styles.infoCell}>
            {getTranslation("DEVIATION_REPORT_PHONE") || "Phone"}:{" "}
            {deviation.contractorForemanPhone || ""}
          </Text>
          <Text style={styles.infoCell}>
            {getTranslation("DEVIATION_LABEL_REPORT_NUMBER") ||
              "Report number"}
            : {deviation.deviationNumber || ""}
          </Text>
          <Text style={styles.infoCell}>
            {getTranslation("DEVIATION_LABEL_DATE") || "Date"}:{" "}
            {formatFinnishDate(new Date(deviation.deviationDate))}
          </Text>
        </View>

        {section(
          1,
          getTranslation("DEVIATION_SECTION_TYPE") || "Deviation type",
          <>
            <View style={styles.optionGrid}>
              {metadata.types.map((item) => (
                <Text key={item.id} style={styles.option}>
                  {optionMark(item.id === deviation.deviationType)} {item.label}
                </Text>
              ))}
            </View>
            <View style={styles.optionGrid}>
              {metadata.targets.map((item) => (
                <Text key={item.id} style={styles.option}>
                  {optionMark(item.id === deviation.deviationTarget)}{" "}
                  {item.label}{" "}
                  {item.id === metadata.otherTargetId
                    ? deviation.deviationTargetDescription || ""
                    : ""}
                </Text>
              ))}
            </View>
          </>,
        )}

        {section(
          2,
          getTranslation("DEVIATION_SECTION_OBSERVER") ||
            "Observation made by",
          <View style={styles.optionGrid}>
            {option(
              deviation.deviationSource === 1,
              getTranslation("DEVIATION_SOURCE_OWN") || "Own observation",
            )}
            {option(
              deviation.deviationSource === 2,
              getTranslation("DEVIATION_SOURCE_CLIENT") ||
                "Client observation",
            )}
            {option(
              deviation.deviationSource === 3,
              `${getTranslation("DEVIATION_SOURCE_OTHER") || "Other, specify"} ${deviation.deviationSourceDescription || ""}`,
            )}
          </View>,
        )}

        {section(
          3,
          getTranslation("DEVIATION_SECTION_DESCRIPTION") ||
            "Deviation description",
          <>
            <Text style={styles.fieldLabel}>
              {getTranslation("DEVIATION_LABEL_RELATED_ENTRY") ||
                "Entry related to the deviation"}
            </Text>
            <Text style={styles.fieldValue}>
              {deviation.phaseDescription || ""}
            </Text>
            <Text style={styles.fieldLabel}>
              {getTranslation("DEVIATION_LABEL_DESCRIPTION_LOCATION") ||
                "Description and location of the deviation"}
            </Text>
            <Text style={styles.fieldValue}>
              {deviation.description || ""}
            </Text>
            <View style={styles.optionGrid}>
              {option(
                deviation.effectToQuality === 1,
                getTranslation("DEVIATION_EFFECTS_QUALITY") ||
                  "Affects quality",
              )}
              {option(
                deviation.effectToQuality === 2,
                getTranslation("DEVIATION_NO_QUALITY_EFFECT") ||
                  "Does not affect quality",
              )}
              {option(
                deviation.effectToValue === 1,
                getTranslation("DEVIATION_EFFECTS_VALUE") ||
                  "Affects reductions in value",
              )}
              {option(
                deviation.effectToValue === 2,
                getTranslation("DEVIATION_NO_VALUE_EFFECT") ||
                  "Does not affect reductions in value",
              )}
            </View>
          </>,
        )}

        {section(
          4,
          getTranslation("DEVIATION_SECTION_ACTIONS") ||
            "Actions caused by the deviation",
          <View style={styles.optionGrid}>
            {option(
              deviation.deviationAction === 1,
              getTranslation("DEVIATION_ACTION_REJECT_REDO") ||
                "Reject / redo",
            )}
            {option(
              deviation.deviationAction === 2,
              getTranslation("DEVIATION_ACTION_RETURN_CLAIM") ||
                "Return / make a claim",
            )}
            {option(
              deviation.deviationAction === 3,
              getTranslation("DEVIATION_ACTION_REPAIR") || "Repair",
            )}
            {option(
              deviation.deviationAction === 4,
              getTranslation("DEVIATION_ACTION_USE_AS_IS") || "Use as is",
            )}
          </View>,
        )}

        {section(
          5,
          getTranslation("DEVIATION_SECTION_COSTS") ||
            "Estimated costs caused by the deviation",
          <Text>{deviation.costs || ""}</Text>,
        )}
        {section(
          6,
          getTranslation("DEVIATION_SECTION_REPAIR") ||
            "Deviation repair (repair plan when needed)",
          <Text>{deviation.fixPlan || ""}</Text>,
        )}
        {section(
          7,
          getTranslation("DEVIATION_SECTION_PREVENTION") ||
            "How a similar deviation can be prevented",
          <Text>{deviation.preventPlan || ""}</Text>,
        )}
        {section(
          8,
          getTranslation("DEVIATION_SECTION_ACCEPTANCE") ||
            "Acceptance of actions (supervisor signature)",
          <View>
            {option(
              deviation.deviationAccept === 1,
              getTranslation("DEVIATION_ACCEPT_USE_AS_IS") ||
                "Client accepts use as is",
            )}
            {option(
              deviation.deviationAccept === 2,
              getTranslation("DEVIATION_ACCEPT_REPAIR") ||
                "Client accepts the proposed repair",
            )}
            {option(
              deviation.deviationAccept === 3,
              getTranslation("DEVIATION_REJECT_REPAIR") ||
                "Client rejects the proposed repair",
            )}
          </View>,
        )}
        {section(
          9,
          getTranslation("DEVIATION_SECTION_SUPERVISOR_COMMENTS") ||
            "Supervisor comments",
          <Text>{deviation.supervisorComments || ""}</Text>,
        )}
        {section(
          10,
          getTranslation("DEVIATION_SECTION_REPAIR_CONFIRMATION") ||
            "Repair confirmation",
          <View>
            <View style={styles.signatureRow}>
              <Text style={styles.signature}>
                {getTranslation("DEVIATION_SIGNATURE_REPAIR_RESPONSIBLE") ||
                  "Date / signature of person responsible for repair"}
              </Text>
              <Text style={styles.signature}>
                {getTranslation("DEVIATION_SIGNATURE_SUPERVISOR") ||
                  "Date / supervisor signature"}
              </Text>
            </View>
            <View style={styles.signatureRow}>
              <Text style={styles.signature}>
                {getTranslation("DEVIATION_SIGNATURE_AUTHOR") || "Author"}
              </Text>
              <Text style={styles.signature}>
                {getTranslation("DEVIATION_SIGNATURE_APPROVER") || "Approver"}
              </Text>
            </View>
          </View>,
        )}
      </Page>
    </Document>
  );
}
